import { sanitizeAnalyticsProperties, sanitizeUrlForAnalytics } from "@/lib/privacy/urlSanitization";

export const ANALYTICS_EVENTS = {
  pageView: "page_view",
  ctaClick: "cta_click",
  quizStart: "quiz_start",
  quizComplete: "quiz_complete",
  leadCapture: "lead_capture",
  domainHandoff: "domain_handoff",
  voiceAssessmentSampleView: "voice_assessment_sample_view",
  voiceAssessmentSampleDownload: "voice_assessment_sample_download",
  voiceAssessmentBookingClick: "voice_assessment_booking_click",
  loginStarted: "login_started",
  loginCompleted: "login_completed",
  checkoutStarted: "checkout_started",
  purchaseCompleted: "purchase_completed",
  localLessonPageView: "local_lesson_page_view",
  bookingStarted: "booking_started",
  bookingLocationSelected: "booking_location_selected",
  bookingCompleted: "booking_completed",
  paymentStarted: "payment_started",
  paymentCompleted: "payment_completed",
  lessonConfirmed: "lesson_confirmed",
  evaluationAttended: "evaluation_attended",
  planDelivered: "plan_delivered",
  videoFeedbackPurchased: "video_feedback_purchased",
  videoSubmitted: "video_submitted",
  videoFeedbackDelivered: "video_feedback_delivered",
  upsellViewed: "upsell_viewed",
  upsellPurchased: "upsell_purchased",
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export type AnalyticsEventProperties = {
  page?: string | null;
  offer?: string | null;
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  content?: string | null;
  referrer?: string | null;
  user_id?: string | null;
  session_id?: string | null;
  product_type?: string | null;
  checkout_type?: "guest" | "account" | "portal" | null;
  booking_status?: string | null;
  value?: number | null;
  currency?: string | null;
  [key: string]: unknown;
};

export const LEGACY_EVENT_ALIASES: Record<string, AnalyticsEventName> = {
  page_home_view: ANALYTICS_EVENTS.pageView,
  page_blog_view: ANALYTICS_EVENTS.pageView,
  page_blog_article_view: ANALYTICS_EVENTS.pageView,
  page_find_your_blocker_view: ANALYTICS_EVENTS.pageView,
  page_book_evaluation_view: ANALYTICS_EVENTS.pageView,
  checkout_success_view: ANALYTICS_EVENTS.pageView,
  video_feedback_buy_complete: ANALYTICS_EVENTS.videoFeedbackPurchased,
  evaluation_booking_complete: ANALYTICS_EVENTS.bookingCompleted,
  blocker_diagnostic_started: ANALYTICS_EVENTS.quizStart,
  blocker_diagnostic_completed: ANALYTICS_EVENTS.quizComplete,
  blocker_diagnostic_email_captured: ANALYTICS_EVENTS.leadCapture,
  checkout_manual_submit: ANALYTICS_EVENTS.checkoutStarted,
  direct_checkout_attempt: ANALYTICS_EVENTS.checkoutStarted,
  video_feedback_buy_start: ANALYTICS_EVENTS.checkoutStarted,
  portal_upsell_view: ANALYTICS_EVENTS.upsellViewed,
};

export function resolveCanonicalEventName(name: string): AnalyticsEventName | null {
  if ((Object.values(ANALYTICS_EVENTS) as string[]).includes(name)) {
    return name as AnalyticsEventName;
  }
  if (LEGACY_EVENT_ALIASES[name]) return LEGACY_EVENT_ALIASES[name];
  if (name.startsWith("page_") && name.endsWith("_view")) return ANALYTICS_EVENTS.pageView;
  if (name.startsWith("cta_") || name.endsWith("_click") || name.includes("_cta_")) {
    return ANALYTICS_EVENTS.ctaClick;
  }
  return null;
}

export function normalizeAnalyticsProperties(
  properties: AnalyticsEventProperties | undefined,
): AnalyticsEventProperties {
  const payload: AnalyticsEventProperties = { ...(properties ?? {}) };

  if (!payload.page && typeof window !== "undefined") {
    payload.page = window.location.pathname;
  }
  if (!payload.referrer && typeof document !== "undefined") {
    payload.referrer = document.referrer ? sanitizeUrlForAnalytics(document.referrer) : "direct";
  }

  if (!payload.source && typeof payload.utm_source === "string") {
    payload.source = payload.utm_source;
  }
  if (!payload.medium && typeof payload.utm_medium === "string") {
    payload.medium = payload.utm_medium;
  }
  if (!payload.campaign && typeof payload.utm_campaign === "string") {
    payload.campaign = payload.utm_campaign;
  }
  if (!payload.content && typeof payload.utm_content === "string") {
    payload.content = payload.utm_content;
  }

  return sanitizeAnalyticsProperties(payload) as AnalyticsEventProperties;
}
